import { useMutation } from '@tanstack/react-query';
import Decimal from 'decimal.js';
import { AxiosError } from 'axios';

import { Room } from 'src/shared/types/room';
import { Term } from 'src/shared/types/booking-types';

import { BasketProductDto } from '../../../api/basketService/dto/BasketProduct.dto';
import { CreateBasketDto } from '../../../api/basketService/dto/CreateBasket.dto';
import { useUpdateBasket } from '../../../lib/hooks/use-patch-update-basket';
import { useCreateBasket } from '../../../lib/hooks/use-post-create-basket';
import { getCookie } from '../../../utils/get-cookie';
import { UserChoices } from '../../../shared/types/user-choices';
import { PaymentPlanDto } from '../../../api/paymentPlanService/dto/PaymentPlanDto';
import { ApiBasket } from '../../../api/basketService';
import { IsValidRebookerResponse } from '../../../api/rebookersService';
import { StudentBookingErrorResponse } from '../../../api/types';
import { RoommateData } from '../../../features/multi-occupancy/types';

const mutationKey = ['submitBasket'];

type BasketVariables = {
    room: Room;
    paymentPlanId: string | null;
    userChoices: UserChoices;
    paymentPlan: PaymentPlanDto;
    basket?: ApiBasket;
    isValidRebooker: IsValidRebookerResponse | null;
    agentCode: string | null;
    agentStudentId: string | null;
    cbl: boolean;
    paymentOptions: Term[];
};

export const usePostSubmitBasket = () => {
    const { mutateAsync: createBasket } = useCreateBasket();
    const { mutateAsync: updateBasketAsync } = useUpdateBasket();

    return useMutation<
        void,
        AxiosError<
            StudentBookingErrorResponse<{
                room?: string[];
            }>
        >,
        BasketVariables
    >({
        mutationKey,
        mutationFn: async ({
            room,
            paymentPlan,
            userChoices,
            basket,
            isValidRebooker,
            agentCode,
            agentStudentId,
            cbl,
            paymentPlanId,
            paymentOptions,
        }) => {
            if (!room || !paymentPlan || !userChoices.term) return;

            const product: BasketProductDto = new BasketProductDto({
                id: room.id,
                room_code: room.room_code,
                academic_year: room.academic_year,
                building_id: room.building_id,
                development_id: room.development_slug,
                price: new Decimal(room.monthly_price ?? room.base_price ?? 0),
                currency: room.currency,
                deposit_prepaid: room.deposit_prepaid,
                advance_payment_weeks: room.advance_payment_weeks as number,
                beds: room.number_of_beds as number,
                deposit: room.deposit_prepaid
                    ? new Decimal(0)
                    : new Decimal(room.base_price).times(room.advance_payment_weeks),
                selected_students: userChoices.numberOfStudents as number,
                term: userChoices.term.name,
                payment_plan_id: userChoices.instalmentId as string,
                instalment_type: userChoices.instalmentType as string,
                cbl,
                stl: Boolean(paymentPlanId),
                occupants: basket?.room_mates || [],
                selected_price: {
                    fee: paymentPlan.weeklyPrice,
                    today: paymentPlan.paymentOptions[0].price,
                    non_discounted: paymentPlan.subtotal,
                },
                legal_representative: null,
                promotion_id: paymentPlan.promotion ? paymentPlan.promotion.id : null,
            });

            const vitaGclid = getCookie('_vita_gclid');
            const vitaUtmMedium = getCookie('_vita_utm_medium');
            const vitaUtmSource = getCookie('_vita_utm_source');
            const tracking = {
                lead_source: vitaGclid,
                lead_medium: vitaUtmMedium,
                lead_campaign: vitaUtmSource,
            };

            if (basket?.id) {
                let basketRoommates: RoommateData[] = [];

                if (userChoices.numberOfStudents > 1 && basket?.room_mates) {
                    basketRoommates = basket.room_mates;
                }

                await updateBasketAsync({
                    basketId: basket.id,
                    updatedAt: basket.updated_date,
                    basketData: {
                        user: null,
                        products: [product],
                        room_mates: basketRoommates,
                        options: {
                            ...basket.options,
                            agentCode,
                            agentStudentId,
                            rebooker: isValidRebooker ? isValidRebooker.rebooker : false,
                            fillerWeekEligible: isValidRebooker
                                ? isValidRebooker.filler_week_eligible
                                : false,
                            referrerCode: null,
                        },
                        tracking,
                        vendor: 'studentbookingvendor',
                        payment_options_snapshot: paymentOptions,
                    },
                });
            } else {
                await createBasket(
                    new CreateBasketDto({
                        user: null,
                        products: [product],
                        options: {
                            agentCode,
                            agentStudentId,
                            rebooker: false,
                            fillerWeekEligible: false,
                            referrerCode: null,
                        },
                        tracking,
                        vendor: 'studentbookingvendor',
                        payment_options_snapshot: paymentOptions,
                    })
                );
            }
        },
    });
};
